import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { ProductType } from '../models/product-type.model';
import { Products } from '../models/product.model';
import { ProductsService } from '../product.service';

@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        InputTextModule,
        ProgressBarModule,
        SelectModule,
        TagModule,
        TooltipModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        ProductsService,
        SessionStorageService
    ]
})
export class ProductComponent implements OnInit {
    products: Products[] = [];
    filteredProducts: Products[] = [];
    expandedRows: Record<string, boolean> = {};
    isLoading = false;
    totalRecords = 0;
    lastUpdated: Date | null = null;

    searchText = '';
    codeFilter = '';
    typeFilter: ProductType | null = null;

    readonly productTypeOptions = Object.values(ProductType).map(type => ({
        label: type,
        value: type
    }));

    constructor(
        private productsService: ProductsService,
        private messageService: MessageService,
        private router: Router,
        private sessionStorageService: SessionStorageService
    ) {}

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        this.isLoading = true;
        this.productsService.getAllProductsByOwningCompany(this.sessionStorageService.getCompany()?.id ?? 0).subscribe({
            next: data => {
                this.products = data ?? [];
                this.totalRecords = this.products.length;
                this.lastUpdated = new Date();
                this.applyFilters();
                this.isLoading = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Products',
                    detail: 'Error loading products.'
                });
                this.isLoading = false;
            }
        });
    }

    applyFilters(): void {
        const search = this.searchText.trim().toLowerCase();
        const code = this.codeFilter.trim().toLowerCase();
        const type = this.typeFilter;

        this.filteredProducts = this.products.filter(product => {
            const matchesSearch = !search || [
                product.name,
                product.productCode,
                product.description,
                product.productType
            ].some(value => String(value ?? '').toLowerCase().includes(search));
            const matchesCode = !code || String(product.productCode ?? '').toLowerCase().includes(code);
            const matchesType = !type || product.productType === type;
            return matchesSearch && matchesCode && matchesType;
        });
    }

    clearFilters(): void {
        this.searchText = '';
        this.codeFilter = '';
        this.typeFilter = null;
        this.applyFilters();
    }

    addProduct(): void {
        this.router.navigate(['/order/products/add']);
    }

    editProduct(product: Products): void {
        this.router.navigate(['/order/products/edit', product.productID]);
    }

    metrics(): PageMetric[] {
        const variantCount = this.products.reduce((sum, product) => sum + (product.productVariants?.length || 0), 0);
        const serviceCount = this.products.filter(product => product.productType === ProductType.SERVICE).length;
        const averageSellingRate = this.products.length
            ? this.products.reduce((sum, product) => sum + this.primaryVariantSellingRate(product), 0) / this.products.length
            : 0;

        return [
            { label: 'Catalog Size', value: this.totalRecords, hint: 'Active product masters', tone: 'info' },
            { label: 'Visible Rows', value: this.filteredProducts.length, hint: `${this.activeFilterCount()} active filters`, tone: 'success' },
            { label: 'Variants', value: variantCount, hint: 'Variant definitions in workspace', tone: 'warn' },
            { label: 'Avg Sell Rate', value: this.formatCurrency(averageSellingRate), hint: `${serviceCount} service products` }
        ];
    }

    activeFilterCount(): number {
        let count = 0;
        if (this.searchText.trim()) count++;
        if (this.codeFilter.trim()) count++;
        if (this.typeFilter) count++;
        return count;
    }

    resultText(): string {
        return `${this.filteredProducts.length} visible of ${this.totalRecords} products`;
    }

    lastUpdatedText(): string {
        return this.lastUpdated ? `Last refreshed ${this.lastUpdated.toLocaleString()}` : 'Catalog not refreshed yet';
    }

    totalVariantCount(product: Products): number {
        return product.productVariants?.length || 0;
    }

    variantSummary(product: Products): string {
        const variants = product.productVariants || [];
        if (!variants.length) {
            return 'Legacy product-only pricing';
        }

        const codes = variants
            .map(variant => variant.variantCode)
            .filter(Boolean)
            .slice(0, 3)
            .join(', ');
        return codes || `${variants.length} variants ready`;
    }

    pricingHeadline(product: Products): string {
        const variants = product.productVariants || [];
        if (!variants.length) {
            return `Legacy ${this.formatCurrency(this.resolveStandardRate(product))}`;
        }

        return `Std ${this.rateRange(product, variant => this.resolveVariantStandardRate(product, variant))}`;
    }

    buyingRange(product: Products): string {
        return this.rateRange(product, variant => this.resolveVariantBuyingRate(product, variant));
    }

    sellingRange(product: Products): string {
        return this.rateRange(product, variant => this.resolveVariantSellingRate(product, variant));
    }

    currencyLabel(product: Products): string {
        const currency = (product.currencyCode || '').toString().trim().toUpperCase();
        return currency || 'SAR';
    }

    formatPercent(value?: number | null): string {
        return Number(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    primaryVariantSellingRate(product: Products): number {
        const primaryVariant = product.productVariants?.[0];
        return this.resolveVariantSellingRate(product, primaryVariant);
    }

    private rateRange(product: Products, resolver: (variant?: Products['productVariants'][number]) => number): string {
        const variants = product.productVariants || [];
        if (!variants.length) {
            return this.formatCurrency(resolver(undefined));
        }

        const values = variants.map(variant => resolver(variant));
        const min = Math.min(...values);
        const max = Math.max(...values);
        return min === max
            ? this.formatCurrency(min)
            : `${this.formatCurrency(min)} - ${this.formatCurrency(max)}`;
    }

    private resolveStandardRate(product: Products): number {
        const primaryVariant = product.productVariants?.[0];
        return this.resolveVariantStandardRate(product, primaryVariant);
    }

    private resolveVariantStandardRate(product: Products, variant?: Products['productVariants'][number]): number {
        return this.resolveAmount(variant?.rateAdjustment, product.standardRate, 0);
    }

    private resolveVariantBuyingRate(product: Products, variant?: Products['productVariants'][number]): number {
        return this.resolveAmount(variant?.buyingRateAdjustment, variant?.rateAdjustment, product.buyingRate, product.standardRate, 0);
    }

    private resolveVariantSellingRate(product: Products, variant?: Products['productVariants'][number]): number {
        return this.resolveAmount(variant?.sellingRateAdjustment, variant?.rateAdjustment, product.sellingRate, product.standardRate, 0);
    }

    private resolveAmount(...candidates: Array<number | null | undefined>): number {
        for (const candidate of candidates) {
            if (candidate !== null && candidate !== undefined && !Number.isNaN(Number(candidate))) {
                return Number(candidate);
            }
        }
        return 0;
    }

    formatCurrency(value?: number | null): string {
        return Number(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    showDate(date?: Date): string {
        return date ? new Date(date).toLocaleDateString() : '-';
    }
}
